/**
 * AssessmentFiller — RN completes an Admission / Supervisory / Re-Admission
 * assessment on the agency PDF. Drafts save in place; completing an Admission
 * or Re-Admission first asks for a short clinical summary (CMS-485 pre-fill).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import FillableFormFiller from "@/components/FillableFormFiller";
import ClinicalSummaryDialog from "./ClinicalSummaryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLINICAL = new Set(["admission", "readmission"]);

export default function AssessmentFiller({ assessmentId, onClose, onCompleted }) {
  const [record, setRecord] = useState(null);
  const [pending, setPending] = useState(null); // values awaiting clinical summary
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/assessments/${assessmentId}`)
      .then((r) => setRecord(r.data))
      .catch((err) => { toast.error(err.response?.data?.detail || "Could not open assessment"); onClose(); });
  }, [assessmentId, onClose]);

  const complete = async (values, clinical_summary) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assessments/${assessmentId}/complete`, { values, clinical_summary });
      if (r.data.visit_complete === false) {
        toast.success(`Form completed — ${r.data.remaining_forms} more form${r.data.remaining_forms === 1 ? "" : "s"} to finish in this visit`);
      } else {
        toast.success(r.data.referral_marked ? "Visit completed — referral marked “Assessment Completed” and PDFs saved to Documents" : "Visit completed — signed PDF saved to the client's Documents");
      }
      if (r.data.restarted_forms?.length) {
        toast.info(`New cycle started: ${r.data.restarted_forms.map((f) => f.template_title).join(", ")} — find it under In progress to sign the fresh original.`, { duration: 8000 });
      }
      setPending(null);
      onCompleted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not complete assessment");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const onSubmitValues = async (values) => {
    if (!CLINICAL.has(record.assessment_type) || record.group_remaining > 0) {
      await complete(values, null); // summary is asked once, on the last form of the visit
      return true;
    }
    await axios.put(`${API}/assessments/${assessmentId}/draft`, { values }).catch(() => {});
    try {
      const r = await axios.post(`${API}/assessments/${assessmentId}/summary-suggestion`, { values });
      setSuggestion(r.data.suggestion || {});
    } catch { setSuggestion({}); }
    setPending(values);
    return false;
  };

  if (!record) return null;
  return (
    <>
      {!pending && (
        <FillableFormFiller
          open
          onClose={onClose}
          form={record}
          title={`${record.recurring_count ? `${record.template_title} (${record.revision_label || "Original"})` : `${record.type_label} Assessment`} — ${record.client_name}`}
          pdfEndpoint={`${API}/assessments/${assessmentId}/pdf`}
          onSaveDraft={async (values) => { await axios.put(`${API}/assessments/${assessmentId}/draft`, { values }); }}
          onSubmitValues={onSubmitValues}
          submitLabel="Complete assessment"
          confirmSubmit="Once completed, the signed PDF is filed to the client's Documents and the form can no longer be edited. Use “Work on later” if you still need to make changes."
        />
      )}
      <ClinicalSummaryDialog
        open={!!pending}
        onClose={() => { setRecord((r) => ({ ...r, default_values: pending })); setPending(null); }}
        suggestion={suggestion}
        typeLabel={record.type_label}
        busy={busy}
        onConfirm={(summary) => complete(pending, summary).catch(() => {})}
      />
    </>
  );
}
